<template>
  <div
    class="prevent-select"
    v-on:contextmenu.stop.prevent="contextMenuClick"
    v-on:click.ctrl.prevent="toggleItemSelection(false)"
    v-on:click.shift.prevent="toggleItemSelection(true)"

    :style="{ opacity: isDragOver ? 0.5 : 1 }"
    @dragover.stop.prevent="handleDomDragOver"
    @dragenter.stop.prevent="onDragEnter"
    @dragleave.stop.prevent="onDragLeave"
    @drop.prevent="handleDomDrop"

    :draggable="isDraggableItem(item, asSearchResult)"
    @dragstart="onDragStart($event, item)"
    @dragend="onDragEnd($event, item)"
  >

    <div class="d-flex justify-center align-center" style="width: 200px; height: 112px;">
      <router-link
        v-bind:to="getNodeLink"
        class="node-grid-item-icon"
        style="display: flex;"
        :tabindex="-1"
        :aria-label="item.name"
      >
        <div v-if="getClientLogo && !isDeleted" v-bind:style="getClientLogo"/>
        <v-icon v-if="!getClientLogo || isDeleted" :size="86">{{ getNodeIconName }}</v-icon>
      </router-link>
    </div>

    <div class="d-flex justify-space-between" style="margin-top: -112px; max-height: 24px; min-height: 24px;">
      <NodeItemSelect
        v-if="!asSearchResult"
        v-on:toggleItemSelection="toggleItemSelection"
        v-bind:node="item"
        style="width: 32px; margin-top: -4px;"
      />
      <div class="d-flex">
        <v-chip
          v-if="isArchived"
          v-bind:title="$t('routes.node.statusArchived')"
          size="small"
          label
        >
          <v-icon size="small" icon="mdi-package-variant-closed-check"></v-icon>
        </v-chip>
        <v-chip v-if="item.retentionperiod > 0"
                v-bind:title="retentionPeriod" label size="small" class="ml-1">
          <v-icon size="small" icon="mdi-delete-clock"></v-icon>
        </v-chip>
      </div>
    </div>

    <div class="d-flex justify-end" style="margin-top: 65px;height: 24px;">
      <v-chip v-if="item.teamonly === accessType.TEAM_ONLY"
              v-bind:title="$t('routes.node.accessTypeTeamOnly')" label size="small">
        <v-icon size="small" icon="mdi-account-group-outline"></v-icon>
      </v-chip>

    </div>

    <div class="grid-node-name text-truncate mt-2">
      <v-tooltip location="bottom" open-delay="200" :aria-label="item.name">
        <template v-slot:activator="{ props }">
          <div v-bind="props" class="text-truncate" style="width: 100%;"
               data-cy="list-nodeItem-name">
            <router-link
              :to="getNodeLink"
              class="text-truncate"
              :class="getClass()"
              :aria-label="$t('routes.node.a11y.enterFolder', { name: item.name })"
              :tabindex="0"
            >
              {{ item.name }}
            </router-link>
          </div>
        </template>
        <span>{{ item.name }}</span>
      </v-tooltip>
    </div>

    <div class="d-flex mt-1">
      <div class="flex-grow-1" style="width: 164px;">
        <div class="d-flex flex-column grid-node-attributes">
          <div
            :aria-label="$t('routes.node.a11y.createdAt', { date: isoToDate(item.createdat) })"
          >
            {{ isoToDate(item.createdat) }}
          </div>
          <div class="text-truncate"
               :aria-label="$t('routes.node.a11y.createdBy', { name: getCreatedBy })"
          >
            {{ getCreatedBy }}
          </div>
        </div>
      </div>

      <div class="flex-grow-0">
        <v-btn
          icon
          variant="flat"
          rounded="circle"
          density="comfortable"
          color="rgba(0, 0, 0, 0)"
          width="36"
          height="36"
          @click="contextMenuBtnClick"
          :aria-label="$t('routes.node.a11y.openActionsMenu', { name: item.name })"
        >
          <v-icon icon="mdi-dots-vertical"/>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { useCommonStore } from '@/stores/CommonStore.js'
import NodeItemSelect from '../nodeListView/NodeItemSelect.vue'
import { isoToDate } from '@/components/formatters.js';
import MovableItem from '@/components/mixin/MovableItem.js';
import MovableItemDropzone from '@/components/mixin/MovableItemDropzone.js';

export default {
  name: "GridNodeItem",
  mixins: [MovableItem, MovableItemDropzone],
  emits: ["showContextMenu", "toggleItemSelection", "droppedMovableItems"],
  components: {
    NodeItemSelect,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    asSearchResult: {
      type: Boolean,
      default: false,
    },
    isDeleted: {
      type: Boolean,
      default: false,
    }
  },
  mounted: function() {
    // console.log("item", this.item)
  },
  methods: {
    isoToDate,
    toggleItemSelection: function(holdingShift) {
      const payload = {
        holdingShift: holdingShift,
        item: this.item
      };

      this.$emit("toggleItemSelection", payload);
    },
    contextMenuClick(event) {
      const eventData = {
        contextMenuNode: this.item,
        contextMenuX: event.clientX,
        contextMenuY: event.clientY,
      };

      this.$emit('showContextMenu', eventData);
    },
    contextMenuBtnClick(event) {
      const eventData = {
        contextMenuNode: this.item,
        contextMenuX: event.clientX - event.offsetX + event.target.offsetWidth,
        contextMenuY: event.clientY - event.offsetY + event.target.offsetHeight,
      };

      this.$emit('showContextMenu', eventData);
    },
    getClass: function() {
      return {
        // We're likely interested in just showing tenants and/or clients in red, when deleted, unlike rest of the folders
        // which are normally soft deleted.
        'red--text': this.item.deletedat !== null || (
          this.isDeleted && (
            this.item.type === this.nodeTypes.CLIENT ||
            this.item.type === this.nodeTypes.TENANT
          )
        ),
      };
    },
  },
  computed: {
    nodeTypes: function() {
      const commonStore = useCommonStore();
      return commonStore.nodeTypes;
    },
    getNodeIconName: function() {
      switch (this.item.type) {
        case this.nodeTypes.PROJECT: return "mdi-briefcase-clock";
        case this.nodeTypes.CLIENT: return "tw tw-office-building";
        case this.nodeTypes.TENANT: return "mdi-home-assistant";
        default: return "mdi-folder";
      }
    },
    getUpdatedBy: function() {
      if (!this.item.updatedby) {
        return this.$t('common.deletedUser');
      }

      return `${this.item.updatedby.firstname} ${this.item.updatedby.lastname}`;
    },
    getCreatedBy: function() {
      if (!this.item.createdby) {
        return this.$t('common.deletedUser');
      }

      return `${this.item.createdby.firstname} ${this.item.createdby.lastname}`;
    },
    getNodeLink: function() {
      if (this.isDeleted) {
        return { path: `/recycle-bin/${this.item.id}` };
      }

      return { path: '/node/' + this.item.id };
    },
    getClientLogo: function() {
      if (this.item.clientlogourl) {
        return {
          width: '200px',
          height: '112px',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundImage: 'url("'+this.item.clientlogourl+'")'
        };
      }

      return false;
    },
    isArchived: function() {
      return this.item.archived === 1
    },
    accessType() {
      const commonStore = useCommonStore();
      return commonStore.accessType;
    },
    retentionPeriod: function () {
      if (this.item.retentionperiod > 0) {
        const retentionType = this.item.retentiontype.toLowerCase();
        return this.$t('routes.node.retentionPeriod') + ' ' + this.$t(`common.time.${retentionType}`, this.item.retentionperiod);
      }

      return this.$t('routes.node.noRetention');
    },
  }
}
</script>

<style lang="scss" scoped>
.node-grid-item-icon {}
.node-grid-item-icon:hover { text-decoration: none !important; }

.grid-node-name {
  width: 100%;
  font-size: 12px;
  line-height: 1.2;

  a {
    max-width: 100%;
    display: inline-block;
  }
}

.grid-node-attributes {
  font-size: 12px;
  line-height: 1.2;
}
</style>
